import type {
  Address,
  ApplicantSectionV2,
  ApplicationStatusResponse,
  DealSummarySection,
  DscWorkbenchDashboardResponseV2,
  ResidenceSectionV2,
} from "@credit-acceptance/step6-bff-client";

import type { DealIntelligenceCardViewModel, DealTier, SignerSummary } from "./DealIntelligenceCard.types";

// ---------------------------------------------------------------------------
// dealIntelligenceCard.mapping
//
// Pure mapper: DscWorkbenchDashboardResponseV2 → DealIntelligenceCardViewModel.
// No React. Unit-testable and side-effect-free.
// ---------------------------------------------------------------------------

// Known program tiers, keyed by the uppercased scoreProgram string. The BFF
// derives `dealSummary.scoreProgram` server-side from the CAS
// scoringProgramTypeId (parity with dealer-ui's env-based PROGRAM_STATUS map),
// so we only normalise and look it up here. Anything unknown/absent falls back
// to STANDARD (the base tier), which renders no badge.
const TIER_BY_SCORE_PROGRAM: Record<string, DealTier> = {
  STANDARD: "STANDARD",
  SILVER: "SILVER",
  GOLD: "GOLD",
  PLATINUM: "PLATINUM",
};

/**
 * Maps the BFF `dealSummary.scoreProgram` string onto a `DealTier`. Case- and
 * whitespace-insensitive; unrecognised or missing values map to "STANDARD".
 * @param {string | undefined} scoreProgram - The scoreProgram from dealSummary.
 * @returns {DealTier} The resolved tier (defaults to "STANDARD").
 */
export function mapScoreProgramToTier(scoreProgram: string | undefined): DealTier {
  if (!scoreProgram) {
    return "STANDARD";
  }
  return TIER_BY_SCORE_PROGRAM[scoreProgram.trim().toUpperCase()] ?? "STANDARD";
}

/**
 * Formats the dealer summary into the card's combined "Name (ID)" display, e.g.
 * "Digital Contracting of Illinois (1TL)". Falls back to the name alone when the
 * dealer id is absent, and to an empty string when no dealer name is available.
 * @param {DealSummarySection | undefined} dealSummary - The BFF deal summary section.
 * @returns {string} The combined dealer display string, or "".
 */
function formatDealer(dealSummary: DealSummarySection | undefined): string {
  const name = dealSummary?.dealerName?.trim();
  const id = dealSummary?.dealerId?.trim();
  if (!name) {
    return "";
  }
  return id ? `${name} (${id})` : name;
}

/**
 * Formats the application status + phase into the card's combined "Status
 * (phase)" display, e.g. "Pending Deal (New Deal Review)". Falls back to the
 * status alone when the phase is absent, and to an empty string when no status
 * description is available. Sourced from the ApplicationStatus BFF endpoint
 * (separate from the dashboard).
 * @param {ApplicationStatusResponse | undefined} status - The BFF application status response.
 * @returns {string} The combined status display string, or "".
 */
function formatStatus(status: ApplicationStatusResponse | undefined): string {
  const statusDescription = status?.statusDescription?.trim();
  const phaseDescription = status?.phaseDescription?.trim();
  if (!statusDescription) {
    return "";
  }
  return phaseDescription ? `${statusDescription} (${phaseDescription})` : statusDescription;
}

/** USD formatter shared by all advance amounts produced by this module. */
const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/**
 * Formats a raw ISO date string (YYYY-MM-DD) into the display format MM/DD/YYYY.
 * Returns an empty string when the input is absent or not a valid date string.
 * @param {string | undefined} iso - An ISO date string such as "1985-01-15".
 * @returns {string} The formatted date, e.g. "01/15/1985", or "".
 */
function formatDisplayDate(iso: string | undefined): string {
  if (!iso) {
    return "";
  }
  const parts = iso.split("-");
  if (parts.length !== 3) {
    return "";
  }
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

/**
 * Builds the first address display line (street number + street name + apt).
 * Returns an empty string when none of the parts are present.
 * @param {Address | undefined} physical - The physical/CURRENT address record.
 * @returns {string} Formatted address line 1, e.g. "123 Main St, Apt 4B".
 */
function buildAddressLine1(physical: Address | undefined): string {
  if (!physical) {
    return "";
  }
  const street = [physical.streetNumber, physical.streetName].filter(Boolean).join(" ");
  const apt = physical.apartmentNumber ? `, Apt ${physical.apartmentNumber}` : "";
  return `${street}${apt}`;
}

/**
 * Builds the second address display line (city, state zip).
 * Returns an empty string when the address record is absent.
 * @param {Address | undefined} physical - The physical/CURRENT address record.
 * @returns {string} Formatted address line 2, e.g. "Austin, TX 78701".
 */
function buildAddressLine2(physical: Address | undefined): string {
  if (!physical) {
    return "";
  }
  const cityState = [physical.city, physical.state].filter(Boolean).join(", ");
  const zip = physical.zip ?? "";
  return cityState && zip ? `${cityState} ${zip}` : cityState || zip;
}

/**
 * Picks the primary phone number from the residence phones array. Phones are
 * ordered by `displayOrder` (ascending); the first one is the primary. Returns
 * an empty string when no phones are recorded.
 * @param {ResidenceSectionV2["phones"]} phones - The applicant's phone records.
 * @returns {string} The primary phone number string, or "".
 */
function pickPrimaryPhone(phones: ResidenceSectionV2["phones"]): string {
  if (!phones || phones.length === 0) {
    return "";
  }
  const sorted = [...phones].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  return sorted[0].number ?? "";
}

/**
 * Formats a raw SSN/ITIN value as XXX-XX-XXXX for display. Falls back to the
 * raw value when the digit count isn't exactly 9 (partial/malformed data).
 * @param {string | undefined} raw - The raw SSN/ITIN value from Customer Hub.
 * @returns {string} The formatted SSN, or "—" when absent.
 */
function formatSsn(raw: string | undefined): string {
  if (!raw) {
    return "—";
  }
  const digits = raw.replaceAll(/\D/g, "");
  if (digits.length !== 9) {
    return raw;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
}

/**
 * Builds the full display name from the residence section name parts.
 * Joins non-empty parts in order: firstName middleName lastName nameSuffix.
 * @param {ResidenceSectionV2} residence - The applicant's residence section.
 * @returns {string} The display name, e.g. "John A Smith Jr".
 */
function buildFullName(residence: ResidenceSectionV2): string {
  return [residence.firstName, residence.middleName, residence.lastName, residence.nameSuffix]
    .filter(Boolean)
    .join(" ");
}

/**
 * Maps a single `ResidenceSectionV2` onto a `SignerSummary` for the Deal
 * Intelligence Card header. Returns a blank signer summary when the residence
 * section is absent so callers never need to null-check downstream.
 * @param {ResidenceSectionV2 | undefined} residence - The applicant's residence section.
 * @returns {SignerSummary} The signer display data.
 */
function mapResidenceToSignerSummary(residence: ResidenceSectionV2 | undefined): SignerSummary {
  if (!residence) {
    return { name: "", ssn: "—", dob: "", addressLine1: "", addressLine2: "", phone: "" };
  }

  const physical = residence.current;

  return {
    name: buildFullName(residence),
    // ssnOrItin is the resolved SSN/ITIN value from Customer Hub (renamed from
    // `ssn` in step6-bff-client@1.0.287). Formatted as XXX-XX-XXXX; "—" when
    // undefined/absent (no record on file). The view-model field stays `ssn`
    // — this card labels it SSN.
    ssn: formatSsn(residence.ssnOrItin),
    dob: formatDisplayDate(residence.dateOfBirth),
    addressLine1: buildAddressLine1(physical),
    addressLine2: buildAddressLine2(physical),
    phone: pickPrimaryPhone(residence.phones),
  };
}

/**
 * Formats a numeric advance amount as a USD currency string.
 * Returns an empty string when the value is absent or not a finite number.
 * @param {number | undefined} amount - The raw advance amount from dealStatus.
 * @returns {string} Formatted currency string, e.g. "$26,843.95", or "".
 */
function formatAdvance(amount: number | undefined): string {
  if (amount === undefined || amount === null || !Number.isFinite(amount)) {
    return "";
  }
  return usdFormatter.format(amount);
}

/**
 * Determines whether the second signer is present on the deal. Gates on
 * `ApplicantSectionV2.present` (boolean flag added in step6-bff-client@1.0.229).
 * Falls back to checking whether a residence section exists when `present` is
 * not explicitly set, to handle older BFF responses gracefully.
 * @param {ApplicantSectionV2 | undefined} secondSigner - The second signer section.
 * @returns {boolean} True when the second signer should be shown.
 */
function isSecondSignerPresent(secondSigner: ApplicantSectionV2 | undefined): boolean {
  if (!secondSigner) {
    return false;
  }
  // Prefer the explicit `present` flag; fall back to residence existence.
  if (typeof secondSigner.present === "boolean") {
    return secondSigner.present;
  }
  return !!secondSigner.residence;
}

/**
 * Maps a `DscWorkbenchDashboardResponseV2` to a `DealIntelligenceCardViewModel`
 * for the Deal Intelligence Card. The mapper is pure (no React, no side
 * effects) and safe to call with a partial or empty response object.
 * @param {DscWorkbenchDashboardResponseV2} response - The BFF dashboard response.
 * @param {string} approvalId - The deal approval ID (from the URL, not the response,
 * because `response.approvalId` may be absent on partial failures).
 * @param {ApplicationStatusResponse | undefined} status - The application status + phase
 * from the ApplicationStatus endpoint (a separate read). Absent while that call is
 * pending or when it fails, in which case the status renders empty.
 * @returns {DealIntelligenceCardViewModel} The mapped view model.
 */
export function mapDashboardToDealIntelligenceCard(
  response: DscWorkbenchDashboardResponseV2,
  approvalId: string,
  status?: ApplicationStatusResponse
): DealIntelligenceCardViewModel {
  const driver = mapResidenceToSignerSummary(response.driver?.residence);
  const secondSignerPresent = isSecondSignerPresent(response.secondSigner);
  const secondSigner = secondSignerPresent ? mapResidenceToSignerSummary(response.secondSigner?.residence) : undefined;

  return {
    approvalId,
    tier: mapScoreProgramToTier(response.dealSummary?.scoreProgram),
    // Deal status + phase come from the ApplicationStatus endpoint (separate
    // read), combined into a single "Status (phase)" display.
    status: formatStatus(status),
    driver,
    secondSigner,
    dealerName: formatDealer(response.dealSummary),
    advance: formatAdvance(response.dealStatus?.dealerStatementAdvance),
  };
}
